import { useEffect, useRef } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import type { UnifiedWallet } from '../auth/useWallet';
import type { LegacyWalletBalances } from '../hooks/useLegacyWallet';
import {
  useLegacyClaim,
  type LegacyClaimStep,
} from '../hooks/useLegacyClaim';
import { formatOre, formatSol, shortenPubkey } from '../lib/evore';

/**
 * Legacy wallet mining card. Shown when the connected wallet has either an
 * OreMiner PDA on the ORE program (mined directly to wallet, pre-Evore) or a
 * non-empty ORE ATA. Hidden entirely otherwise so non-legacy users see nothing.
 *
 * Claim buttons hit the ORE program directly (no checkpoint preamble for
 * legacy SOL claim — see memory `legacy-ore-mining-mechanics`).
 */
export function LegacyWalletCard({
  wallet,
  balances,
  onAfterAction,
}: {
  wallet: UnifiedWallet;
  balances: LegacyWalletBalances;
  onAfterAction: () => void;
}) {
  const { t } = useTranslation();
  const claim = useLegacyClaim(wallet);

  const stepLabels: Record<LegacyClaimStep, string> = {
    idle: '',
    preparing: t('sunset.card.stepPreparing'),
    signing: t('sunset.card.stepSigning'),
    submitting: t('sunset.card.stepSubmitting'),
    confirming: t('sunset.card.stepConfirming'),
    success: t('sunset.card.stepSuccess'),
    error: '',
  };

  const walletAddress = wallet.publicKey?.toBase58() ?? '';
  const hasClaimableOre = balances.claimableOre > 0n;
  const hasClaimableSol = balances.rewardsSol > 0n;
  const hasAtaOre = balances.ataOre > 0n;

  const busy =
    claim.step !== 'idle' &&
    claim.step !== 'success' &&
    claim.step !== 'error';

  // Fire onAfterAction once per transition INTO 'success'. Same ref guard
  // pattern as SubaccountCard so parent re-renders don't loop.
  const onAfterRef = useRef(onAfterAction);
  onAfterRef.current = onAfterAction;
  const lastStep = useRef<LegacyClaimStep>('idle');
  useEffect(() => {
    if (claim.step === 'success' && lastStep.current !== 'success') {
      onAfterRef.current();
    }
    lastStep.current = claim.step;
  }, [claim.step]);

  return (
    <div className="win98-window">
      <div className="win98-title-bar">
        <div className="win98-title">
          {t('sunset.legacy.title')}
        </div>
      </div>
      <div className="win98-content">
        <div className="win98-mb-md">
          <div className="win98-text-normal win98-text-bold win98-mb-xs">
            {t('sunset.legacy.walletAddress')}
          </div>
          <div className="win98-flex-row-stretch">
            <div
              className="win98-balance-value-large"
              style={{ flex: 1, textAlign: 'left', fontFamily: 'monospace' }}
            >
              {shortenPubkey(walletAddress, 6)}
            </div>
            <button
              className="win98-button win98-button-primary"
              onClick={() => navigator.clipboard.writeText(walletAddress)}
              title={t('sunset.legacy.copyTitle')}
              style={{ padding: '4px 8px' }}
            >
              <i className="fa-solid fa-copy" />
            </button>
          </div>
        </div>

        <div className="win98-section-heading win98-mb-xs">
          {t('sunset.legacy.balances')}
        </div>
        <div className="win98-sunken portfolio-sunken-group win98-mb-md">
          <div className="portfolio-stat-grid">
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.legacy.pendingSol')}
              </span>
              <span
                className={`portfolio-stat-value ${
                  hasClaimableSol ? 'positive' : ''
                }`}
              >
                {formatSol(balances.rewardsSol, 7)}
              </span>
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.legacy.unrefinedOre')}
              </span>
              <span className="portfolio-stat-value win98-text-ore">
                {formatOre(balances.rewardsOre, 9)}
              </span>
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.legacy.refinedOre')}
              </span>
              <span className="portfolio-stat-value win98-text-ore">
                {formatOre(balances.liveRefinedOre, 9)}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="win98-button"
            onClick={() => void claim.claimOre()}
            disabled={busy || !hasClaimableOre}
            style={{ flex: 1, padding: '6px 12px' }}
            title={
              hasClaimableOre
                ? t('sunset.legacy.claimOreTooltip', {
                    amount: formatOre(balances.claimableOre, 6),
                  })
                : t('sunset.legacy.noOreToClaim')
            }
          >
            {busy ? stepLabels[claim.step] : t('sunset.legacy.claimOre')}
          </button>
          <button
            className="win98-button win98-button-primary"
            onClick={() => void claim.claimSol()}
            disabled={busy || !hasClaimableSol}
            style={{ flex: 1, padding: '6px 12px' }}
            title={
              hasClaimableSol
                ? t('sunset.legacy.claimSolTooltip', {
                    amount: formatSol(balances.rewardsSol, 6),
                  })
                : t('sunset.legacy.noSolToClaim')
            }
          >
            {busy ? stepLabels[claim.step] : t('sunset.legacy.claimSol')}
          </button>
        </div>

        {claim.signature && (
          <div
            className="win98-text-normal"
            style={{ fontSize: 11, marginTop: 8 }}
          >
            <i className="fa-solid fa-check" /> {t('sunset.card.lastTx')}{' '}
            <a
              href={`https://solscan.io/tx/${claim.signature}`}
              target="_blank"
              rel="noreferrer"
            >
              {shortenPubkey(claim.signature, 8)}
            </a>
          </div>
        )}

        {claim.error && (
          <div className="win98-modal-error" style={{ marginTop: 8 }}>
            <i className="fa-solid fa-triangle-exclamation" /> {t(claim.error)}
            <button
              className="win98-button"
              onClick={claim.reset}
              style={{ marginLeft: 8, padding: '2px 8px' }}
            >
              {t('sunset.card.dismiss')}
            </button>
          </div>
        )}

        {hasAtaOre && (
          <p
            className="win98-text-normal"
            style={{ fontSize: 11, marginTop: 8 }}
          >
            {t('sunset.legacy.alreadyInWallet', {
              amount: formatOre(balances.ataOre, 6),
            })}
          </p>
        )}
      </div>
    </div>
  );
}
